import {
  BookingRequest,
  BookingRequestVisibilityEnum,
  LocationCompressed,
  Preferences,
  ResourceTypeDto,
  User,
} from '@jooxter/api';
import { IQuickTimeSlot, JxtEventColorsEnum, JxtBookingVisibilityEnum, MID_DAY, fromISO } from '@jooxter/utils';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useWorkHours } from '../../hooks/services/useWorkHours';
import { useFetchResourceTypes } from '../../queries';

export interface IUseCreateAutomaticBookingReturn {
  createAutomaticBooking: (start: string, end: string, location?: LocationCompressed) => BookingRequest | null;
  resourceTypes: ResourceTypeDto[] | undefined;
}

export const useCreateAutomaticBooking = (
  user?: User | null,
  preferences?: Preferences | null
): IUseCreateAutomaticBookingReturn => {
  const { resourceTypes } = useFetchResourceTypes();
  const { t } = useTranslation();
  const { getDayHours, getMorningHours, getAfternoonHours } = useWorkHours(user?.id);

  const createAutomaticBooking = useCallback(
    (start: string, end: string, location?: LocationCompressed): BookingRequest | null => {
      if (!(user?.id && preferences?.resource && location)) {
        console.warn('Missing user, preferences or location for automatic booking creation');
        return null;
      }

      let slot: IQuickTimeSlot | null;
      const resourceType = resourceTypes?.find((rtype) => rtype.id === preferences.resource?.resourceTypeId);
      // convert workplace timezone to location timezone
      const workplaceStart = fromISO(start, user.timezone);
      const workplaceEnd = fromISO(end, user.timezone);
      const bookingStart = workplaceStart.setZone(location.timezone, { keepLocalTime: true });
      const bookingEnd = workplaceEnd.setZone(location.timezone, { keepLocalTime: true });
      const midDay = bookingStart.set({
        hour: MID_DAY,
        minute: 0,
        second: 0,
      });

      if (bookingEnd <= midDay) {
        slot = getMorningHours(bookingStart);
      } else if (bookingStart >= midDay) {
        slot = getAfternoonHours(bookingEnd);
      } else {
        slot = getDayHours(bookingStart);
      }

      if (!(slot && slot.start && slot.end)) {
        return null;
      }

      if (slot.start >= slot.end) {
        return null;
      }

      return {
        summary: t<string>('booking-form.default-summary', {
          organizerFirstName: user.firstname,
          organizerLastName: user.lastname,
        }),
        description: '',
        start: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          dateTime: slot.start.toUTC().toISO()!,
        },
        end: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          dateTime: slot.end.toUTC().toISO()!,
        },
        color: (preferences.defaultBookingColor?.desk as unknown as string) ?? JxtEventColorsEnum.Green,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        organizerId: user.id!,
        resourceId: preferences.resource.id,
        visibility:
          (preferences.defaultBookingVisibility?.desk as unknown as BookingRequestVisibilityEnum) ??
          JxtBookingVisibilityEnum.PUBLIC,
        options: [],
        physicalAttendees: 1,
        attendees: {
          external: [],
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          internal: resourceType?.allowOverlap ? [user.id!] : [],
        },
        participationStatusAttendees: [],
      };
    },
    [
      t,
      resourceTypes,
      getDayHours,
      getMorningHours,
      user?.id,
      user?.firstname,
      user?.lastname,
      user?.timezone,
      getAfternoonHours,
      preferences,
    ]
  );

  return { createAutomaticBooking, resourceTypes };
};
